import shortid from 'shortid';

export default class QueryLine {
    static Operators = {
        // boolean logic
        isTrue: 'is-true',
        isFalse: 'is-false',
        // list ID
        inList: 'in-list',
        notInlist: 'not-in-list',
        // arrays
        isIn: 'is-in',
        isNotIn: 'is-not-in',
        // empty checks
        isEmpty: 'is-empty',
        isNotEmpty: 'is-not-empty',
        isEmptyOrIn: 'is-empty-or-in',
        isEmptyOrNotIn: 'is-empty-or-not-in',
        // dates
        isAbsolute: 'is-absolute',
        isRelative: 'is-relative',
        // range
        isBetween: 'is-between',
        // single comparisons
        isEqual: 'is-equal',
        isGTE: 'is-gte',
        isLTE: 'is-lte',
    };

    static Types = {
        text: 'text',
        datetime: 'datetime',
        numeric: 'numeric',
        boolean: 'boolean',
    };

    /**
     * The order of prefixes is important for cleaning values by importance.
     * See QueryLine.cleanValues.
     * @type {{negation: string, negatedList: string, list: string}}
     */
    static Prefixes = {
        list: '@',
        negatedList: '!@',
        negation: '!',
    };

    static EmptyString = '';

    static NullString = 'null';

    static NotNullString = '!null';

    static EmptyValue = [QueryLine.EmptyString];

    static OperatorLabels = {
        [QueryLine.Operators.isTrue]: 'is true',
        [QueryLine.Operators.isFalse]: 'is false',
        [QueryLine.Operators.inList]: 'is in list',
        [QueryLine.Operators.notInlist]: 'is not in list',
        [QueryLine.Operators.isIn]: 'is in',
        [QueryLine.Operators.isNotIn]: 'is not in',
        [QueryLine.Operators.isEmpty]: 'is empty',
        [QueryLine.Operators.isNotEmpty]: 'is not empty',
        [QueryLine.Operators.isEmptyOrIn]: 'is empty or is in',
        [QueryLine.Operators.isEmptyOrNotIn]: 'is empty or is not in',
        [QueryLine.Operators.isAbsolute]: 'is absolute',
        [QueryLine.Operators.isRelative]: 'is relative',
        [QueryLine.Operators.isBetween]: 'is between',
        [QueryLine.Operators.isEqual]: 'equals',
        [QueryLine.Operators.isGTE]: 'greater or equal to',
        [QueryLine.Operators.isLTE]: 'lower or equal to',
    };

    static TypeOperators = {
        [QueryLine.Types.boolean]: [
            QueryLine.Operators.isTrue,
            QueryLine.Operators.isFalse,
        ],
        [QueryLine.Types.datetime]: [
            QueryLine.Operators.isRelative,
            QueryLine.Operators.isAbsolute,
            QueryLine.Operators.isEmpty,
            QueryLine.Operators.isNotEmpty,
        ],
        [QueryLine.Types.text]: [
            QueryLine.Operators.isIn,
            QueryLine.Operators.isNotIn,
            QueryLine.Operators.inList,
            QueryLine.Operators.notInlist,
            QueryLine.Operators.isEmpty,
            QueryLine.Operators.isEmptyOrIn,
            QueryLine.Operators.isEmptyOrNotIn,
        ],
        [QueryLine.Types.numeric]: [
            QueryLine.Operators.isEqual,
            QueryLine.Operators.isBetween,
            QueryLine.Operators.isGTE,
            QueryLine.Operators.isLTE,
        ],
    };

    static CustomFieldTypes = {
        array: 'array',
        boolean: 'boolean',
        date: 'date',
        datetime: 'datetime',
        integer: 'integer',
        monetary: 'monetary',
        number: 'number',
        string: 'string',
    };

    static StringDelimiters = {
        filter: ';',
        definition: ':',
        values: ',',
        range: '..',
    };

    static AbsoluteDatetimePattern = /^\d{4}-\d{2}-\d{2}/;

    /**
     * Initialize a query line to be used as part of a collection.
     * @param {{}|[]} line
     * @param {boolean=false} isCriteria
     * @param {?FilterSet} filterSet
     * @param {boolean=false} isNewlyAdded
     */
    constructor({
        line = null,
        isCriteria = false,
        filterSet = null,
        isNewlyAdded = false,
    } = {}) {
        /**
         * Internal tracking ID for rendering purposes.
         * @type {string}
         */
        this.$id = shortid.generate();
        /**
         * Raw line data as saved in criteria or filter format.
         * @type {{}|[]}
         */
        this.line = line;
        /**
         * Defines what filter is used
         * @type {?string}
         */
        this.pointer = null;
        /**
         * Line operator.
         * @type {?string}
         */
        this.operator = null;
        /**
         * @type {?FilterDefinition}
         */
        this.config = null;
        /**
         * Values attached to the line.
         * @type {?[]}
         */
        this.values = [];
        /**
         * Determine if the line was added manually or automatically
         * and if it requires immediate UI rendering.
         * @type {boolean}
         */
        this.isNewlyAdded = isNewlyAdded;
        /**
         * Determines if the line is handling criteria
         * @type {boolean}
         */
        this.isCriteria = isCriteria;
        if (!isCriteria && line) {
            this.hydrateFilter({
                line,
                filterSet,
            });
        }
    }

    /**
     * Set flag to notify UI that line requires menus to open immediately when rendered.
     */
    setNewlyAdded() {
        this.isNewlyAdded = true;
    }

    /**
     * Set flag to notify UI that line has been used and doesn't require immediate rendering.
     */
    setIsDirty() {
        this.isNewlyAdded = false;
    }

    /**
     * Check whether the line has a filter type.
     * @return {boolean}
     */
    get hasType() {
        return !!this.type;
    }

    /**
     * Check whether the line has an operator selected.
     * @return {boolean}
     */
    get hasOperator() {
        return !!this.operator;
    }

    /**
     * Check whether the item operator is using a list.
     * @return {boolean}
     */
    get isSelectingList() {
        return [QueryLine.Operators.inList, QueryLine.Operators.notInlist].includes(this.operator);
    }

    /**
     * Check whether the line is a custom field based on the configuration data.
     * @return {boolean}
     */
    get isCustomField() {
        return !!(this.config && this.config.customFieldFormat);
    }

    /**
     * Check whether the line has restrictions defined in a schema.
     * @return {boolean}
     */
    get hasCustomFieldRestrictions() {
        return this.isCustomField && this.config.additionalSchema !== null;
    }

    /**
     * A list of values that are defined as restrictions for the custom field.
     * @return {Array}
     */
    get customFieldAllowedValues() {
        if (this.hasCustomFieldRestrictions) {
            const {allowedValues = null} = this.config.additionalSchema;
            if (allowedValues && Array.isArray(allowedValues)) {
                return allowedValues;
            }
        }
        return [];
    }

    /**
     * Pre-formatted list of selectable options from the allowed values.
     * @return {{label: string, value: string}[]}
     */
    get customFieldOptions() {
        return this.customFieldAllowedValues.map(value => ({
            value,
            label: value,
        }));
    }

    /**
     * Check whether the custom field is a sting type that will only accept one value.
     * @return {boolean}
     */
    get isSingleValueCustomField() {
        return this.isCustomField
            && this.hasCustomFieldRestrictions
            && this.config.customFieldFormat === QueryLine.CustomFieldTypes.string;
    }

    /**
     * Check whether the item operator is comparing to the raw empty state.
     * @return {boolean}
     */
    get isEmpty() {
        return this.operator === QueryLine.Operators.isEmpty;
    }

    /**
     * Check whether the item operator is comparing to the raw not empty state.
     * @return {boolean}
     */
    get isNotEmpty() {
        return this.operator === QueryLine.Operators.isNotEmpty;
    }

    /**
     * Check whether the item operator is comparing to a boolean `true`.
     * @return {boolean}
     */
    get isTrue() {
        return this.operator === QueryLine.Operators.isTrue;
    }

    /**
     * Check whether the item operator is set to a relative date.
     * @return {boolean}
     */
    get isRelativeDatetime() {
        return this.operator === QueryLine.Operators.isRelative;
    }

    /**
     * Check whether the item operator is set to a range comparison normally used for numbers.
     * @return {boolean}
     */
    get isRange() {
        return this.operator === QueryLine.Operators.isBetween;
    }

    /**
     * Check whether the line is new and untouched.
     * @return {boolean}
     */
    get isBlank() {
        return this.pointer === null;
    }

    /**
     * Compute operator options for the current type.
     * @return {{label: string, value: string}[]}
     */
    get operatorOptions() {
        if (this.type) {
            return QueryLine.TypeOperators[this.type]
                .map(op => ({
                    value: op,
                    label: QueryLine.OperatorLabels[op],
                }));
        }
        return [];
    }

    /**
     * Load up filter data from preferences into the query line.
     * @param {string} pointer
     * @param {[]} values
     * @param {string|null} type
     * @param {FilterSet} filterSet
     */
    hydrateFilter({line: [pointer, values, type], filterSet}) {
        this.pointer = pointer;
        this.type = type ? QueryLine.convertDefinitionType(type) : null;
        this.config = QueryLine.getConfigFromSet({
            filterSet,
            pointer,
        });
        if (!this.type && this.config) {
            // convert the custom field type if present otherwise use the config type
            const discriminatedType = this.isCustomField
                ? this.config.customFieldFormat
                : this.config.type;
            this.type = QueryLine.convertDefinitionType(discriminatedType);
        }
        if (this.type) {
            ({
                operator: this.operator,
                values: this.values,
            } = QueryLine.getOperatorAndValuesFromData({
                values,
                type: this.type,
            }));
        } else {
            // TODO how to handle this edge case
            // the filter has no type in the config or in the line data
        }
    }

    /**
     * Get the filter definition for the pointer from the filter set.
     * @param {FilterSet} filterSet
     * @param {string} pointer
     * @return {FilterDefinition}
     */
    static getConfigFromSet({filterSet, pointer}) {
        return filterSet.combinedFilters[pointer];
    }

    /**
     * Convert the FilterDefinition type into one that is usable in the query component.
     * @param {string} type
     * @return {string}
     */
    static convertDefinitionType(type) {
        switch (type) {
            case 'select':
            case QueryLine.Types.text:
            case 'enum':
            case QueryLine.CustomFieldTypes.string:
            case QueryLine.CustomFieldTypes.array:
            default:
                return QueryLine.Types.text;
            case QueryLine.Types.numeric:
            case QueryLine.CustomFieldTypes.number:
            case QueryLine.CustomFieldTypes.integer:
                return QueryLine.Types.numeric;
            case QueryLine.Types.boolean:
                return QueryLine.Types.boolean;
            case QueryLine.Types.datetime:
            case QueryLine.CustomFieldTypes.date:
                return QueryLine.Types.datetime;
        }
    }

    /**
     * Update the line to house the correct definition for the new pointer and
     * reset other fields that can't be kept between different pointers.
     * @param {FilterDefinition} filterSet
     * @param {string} pointer
     */
    updateFilter({filterSet, pointer}) {
        this.pointer = pointer;
        // save additional information
        this.config = QueryLine.getConfigFromSet({
            filterSet,
            pointer,
        });
        // TODO handle custom fields
        // detect type from definition
        this.type = QueryLine.convertDefinitionType(this.config.type);
        // reset UI by mutating properties that are used as :keys
        this.operator = null;
        this.values = [];
        this.$id = shortid.generate();
    }

    /**
     * Determine the operator from the values and filter definition type.
     * @param {[]|null} values
     * @param {string} type
     * @return {{operator: string|null, values: []|null}}
     */
    static getOperatorAndValuesFromData({values, type}) {
        const convertedType = QueryLine.convertDefinitionType(type);
        switch (convertedType) {
            case QueryLine.Types.text:
                return QueryLine.decodeTextFilter(values);
            case QueryLine.Types.numeric:
                return QueryLine.decodeNumericFilter(values);
            case QueryLine.Types.datetime:
                return QueryLine.decodeDatetimeFilter(values);
            case QueryLine.Types.boolean:
                return QueryLine.decodeBooleanFilter(values);
            default:
                return {operator: null, values: null};
        }
    }

    /**
     * Decode the values for the text filter to detect the operator, then clean the values to
     * remove restricted characters.
     * @param {?[]} values
     * @return {{values: [], operator: string}}
     */
    static decodeTextFilter(values) {
        let operator = QueryLine.Operators.isIn;
        if (values === null) {
            return {
                operator,
                values: [],
            };
        }
        const [
            hasNegative,
            hasList,
            hasNegativeList,
        ] = [
            QueryLine.Prefixes.negation,
            QueryLine.Prefixes.list,
            QueryLine.Prefixes.negatedList,
        ].map(prefix => QueryLine.includesPrefix({values, prefix}));
        const hasEmpty = values.some(value => value === QueryLine.EmptyString);
        if (hasEmpty) {
            if (values.length === 1) {
                operator = QueryLine.Operators.isEmpty;
            } else if (hasNegative) {
                operator = QueryLine.Operators.isEmptyOrNotIn;
            } else {
                operator = QueryLine.Operators.isEmptyOrIn;
            }
        } else if (hasNegativeList) {
            operator = QueryLine.Operators.notInlist;
        } else if (hasList) {
            operator = QueryLine.Operators.inList;
        } else if (hasNegative) {
            operator = QueryLine.Operators.isNotIn;
        }
        return {operator, values: QueryLine.cleanValues(values)};
    }

    /**
     * Decode the values for the numeric filter to detect the operator.
     * @param {?[]} values
     * @return {{values: [], operator: string}}
     */
    static decodeNumericFilter(values) {
        let operator = QueryLine.Operators.isEqual;
        const [min, max] = values || [];
        if (values.length > 1) {
            if (min === null && max === null) {
                operator = QueryLine.Operators.isBetween;
            } else if (max === null) {
                operator = QueryLine.Operators.isGTE;
            } else if (min === null) {
                operator = QueryLine.Operators.isLTE;
            } else {
                operator = QueryLine.Operators.isBetween;
            }
        }
        return {operator, values};
    }

    /**
     * Decode the values for the datetime filter to detect the operator.
     * @param {?[]} values
     * @return {{values: [], operator: string}}
     */
    static decodeDatetimeFilter(values) {
        const hasEmpty = values.some(value => value === QueryLine.NullString);
        const hasNotEmpty = values.some(value => value === QueryLine.NotNullString);
        const [min = null, max = null] = values || [];
        const tested = min || max;
        let operator = QueryLine.Operators.isRelative;
        if (hasEmpty) {
            operator = QueryLine.Operators.isEmpty;
        } else if (hasNotEmpty) {
            operator = QueryLine.Operators.isNotEmpty;
        } else if (tested && QueryLine.AbsoluteDatetimePattern.test(tested)) {
            operator = QueryLine.Operators.isAbsolute;
        }
        return {operator, values};
    }

    /**
     * Decode the values for the boolean filter to detect the operator.
     * @param {?[]} values
     * @return {{values: [], operator: string}}
     */
    static decodeBooleanFilter(values) {
        const [value] = values || [true];
        return {
            operator: value === true
                ? QueryLine.Operators.isTrue
                : QueryLine.Operators.isFalse,
            values,
        };
    }

    /**
     * Set the operator for the item and reset the value to null.
     * @param {string} operator
     */
    setOperator(operator) {
        this.operator = operator;
        this.setValues(null);
    }

    /**
     * Set the pointer for the item
     * @param {string} pointer
     */
    setPointer(pointer) {
        this.pointer = pointer;
    }

    /**
     * Get the values of the item.
     * @return {null|[]}
     */
    getValues() {
        return this.values;
    }

    /**
     * Set the values of the item.
     * @param {[]} values
     */
    setValues(values) {
        this.values = values;
    }

    /**
     * Get the payload for the current line. Returns null if the minimum required information is
     * missing.
     * @return {null|[]}
     */
    getPayload() {
        if ((!this.pointer && !this.hasOperator)
            || this.values === null) {
            // skip payload for this item
            return null;
        }
        if (this.isCriteria) {
            console.error('Criteria not supported');
            // TODO
        }
        return [
            this.pointer,
            this.convertValuesToFilter(),
            this.type,
        ];
    }

    /**
     * Convert the line values into a serialized structure compatible with filters.
     * @return {[]}
     */
    convertValuesToFilter() {
        switch (this.type) {
            case QueryLine.Types.text:
            default:
                return this.textToFilter();
            case QueryLine.Types.numeric:
                return this.numericToFilter();
            case QueryLine.Types.datetime:
                return this.datetimeToFilter();
            case QueryLine.Types.boolean:
                return this.booleanToFilter();
        }
    }

    /**
     * Prefix all values with the provided string.
     * @param {*[]} values
     * @param {string} prefix
     * @return {string[]}
     */
    static prefixValues({values, prefix}) {
        return (values || []).map(value => `${prefix}${value}`);
    }

    /**
     * Remove the prefix from all the provided values.
     * @param {*[]} values
     * @param {string} prefix
     * @return {*[]}
     */
    static unPrefixValues({values, prefix}) {
        return values.map(value => value.replace(new RegExp(`^${prefix}`), ''));
    }

    /**
     * Check whether one of the values includes the prefix.
     * @param {*[]} values
     * @param {string} prefix
     * @return {boolean}
     */
    static includesPrefix({values, prefix}) {
        return (values || []).some(value => value.indexOf(prefix) === 0);
    }

    /**
     * Return the values if they are an array, or an empty array otherwise.
     * @return {*[]|Array}
     */
    get coalesceValuesAsArray() {
        return this.values || [];
    }

    /**
     * Clean the provided values of prefixes used for defining negations or lists.
     * @param {[]} values
     * @return {string[]}
     */
    static cleanValues(values) {
        const pattern = Object.values(QueryLine.Prefixes)
            .map(prefix => `^${prefix}`)
            .join('|');
        return values
            .filter(value => value !== '')
            .map(value => value.replace(new RegExp(pattern), ''));
    }

    /**
     * Convert the text content of the item into a filter structure based on the operator.
     * @return {null|[]|*}
     */
    textToFilter() {
        switch (this.operator) {
            case QueryLine.Operators.isIn:
                return this.values;
            case QueryLine.Operators.isNotIn:
                return QueryLine.prefixValues({
                    values: this.values,
                    prefix: QueryLine.Prefixes.negation,
                });
            case QueryLine.Operators.inList:
                return QueryLine.prefixValues({
                    values: this.values,
                    prefix: QueryLine.Prefixes.list,
                });
            case QueryLine.Operators.notInlist:
                return QueryLine.prefixValues({
                    values: this.values,
                    prefix: QueryLine.Prefixes.negatedList,
                });
            case QueryLine.Operators.isEmpty:
                return QueryLine.EmptyValue;
            case QueryLine.Operators.isEmptyOrIn:
                return [...QueryLine.EmptyValue, ...this.coalesceValuesAsArray];
            case QueryLine.Operators.isEmptyOrNotIn:
                return [
                    ...QueryLine.EmptyValue,
                    ...QueryLine.prefixValues({
                        values: this.values,
                        prefix: QueryLine.Prefixes.negation,
                    }),
                ];
            default:
                return QueryLine.EmptyValue;
        }
    }

    /**
     * Convert the numeric content of the item into a filter structure based on the operator.
     * @return {T[]|[*]|?*[]|*[]}
     */
    numericToFilter() {
        /**
         * Used to enforce the array having a null as the first or last position for the
         * GTE/LTE operators.
         * @param {Function} enforcer
         * @returns {[]}
         */
        const enforceOneSidedRange = (enforcer) => {
            const values = this.coalesceValuesAsArray;
            if (values.length === 2) {
                return values;
            }
            return enforcer(values);
        };
        switch (this.operator) {
            case QueryLine.Operators.isEqual:
            case QueryLine.Operators.isBetween:
            default:
                return this.values;
            case QueryLine.Operators.isGTE:
                return enforceOneSidedRange(values => values.concat(null));
            case QueryLine.Operators.isLTE:
                return enforceOneSidedRange(values => [null].concat(values));
        }
    }

    /**
     * Convert the datetime values into a filter structure.
     * @return {[]}
     */
    datetimeToFilter() {
        if (this.operator === QueryLine.Operators.isEmpty) {
            return [QueryLine.NullString];
        }
        if (this.operator === QueryLine.Operators.isNotEmpty) {
            return [QueryLine.NotNullString];
        }
        return this.coalesceValuesAsArray;
    }

    /**
     * Convert the boolean values into a filter structure.
     * @return {boolean[]}
     */
    booleanToFilter() {
        return [this.isTrue];
    }
}
